<template>
  <section class="ticket-section">
    <div class="files-header">
      <h2 class="ticket-section-title">Dateien</h2>

      <!-- Nur-Artikelfoto-Modus (Eingelangt-Wizard): direkter Upload ohne Kategorie-Dialog.
           Zwei Wege: „Foto aufnehmen" (capture → Kamera) und „Galerie" (Datei/Galerie).
           Das reine accept="image/*" beim Kamera-Input hält die direkte Kamera-Option (iOS) offen. -->
      <span v-if="artikelfotoOnly" class="files-upload-group">
        <label class="btn btn-outline-secondary btn-sm files-upload-btn" :class="{ disabled: working }">
          {{ working ? 'Lädt…' : 'Foto aufnehmen' }}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            class="d-none"
            :disabled="working"
            @change="onPickArtikelfotos"
          />
        </label>
        <label class="btn btn-outline-secondary btn-sm files-upload-btn" :class="{ disabled: working }">
          Galerie
          <input
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            class="d-none"
            :disabled="working"
            @change="onPickArtikelfotos"
          />
        </label>
      </span>

      <!-- Detailseite: voller Upload-Dialog mit Kategorie-Auswahl. -->
      <button v-else type="button" class="btn btn-outline-secondary btn-sm" :disabled="working" @click="onUpload">
        Datei hochladen
      </button>
    </div>

    <ul v-if="files.length" class="files-list">
      <li v-for="f in displayedFiles" :key="f.id" class="file-item">
        <button type="button" class="file-open" @click="onOpenFile(f.id)">
          <span class="file-name" :title="f.description">{{ f.description }}</span>
          <span class="file-meta">
            {{ formatDate(f.uploaded_at) }}<template v-if="f.user_name"> · {{ f.user_name }}</template>
          </span>
        </button>
        <button
          type="button"
          class="file-delete-btn"
          :disabled="working"
          title="Datei löschen"
          aria-label="Datei löschen"
          @click="onDelete(f)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg" width="16" height="16"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M3 6h18" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button>
      </li>
    </ul>
    <p v-else class="files-empty">Noch keine Dateien.</p>

    <button
      v-if="files.length > FILES_PREVIEW"
      type="button"
      class="btn btn-link files-more-btn"
      @click="showAll = !showAll"
    >{{ showAll ? 'Weniger anzeigen' : `Mehr anzeigen (${files.length})` }}</button>

    <p v-if="error" class="error file-error">{{ error }}</p>

    <FileUploadModal v-if="!artikelfotoOnly" ref="uploadModal" />
    <ConfirmModal ref="confirmModal" title="Datei löschen" confirmLabel="Löschen" />
  </section>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import FileUploadModal from './FileUploadModal.vue';
import ConfirmModal from './ConfirmModal.vue';
import { fetchFileBlob, deleteFile, uploadFile, type TicketFile } from '../services/tickets';

/**
 * Wiederverwendbarer Dateien-Bereich eines Tickets: Tabelle (Bezeichnung/Uploadzeit/
 * Benutzer), Öffnen im neuen Tab (Bearer→Blob), Löschen mit Rückfrage, „Mehr anzeigen"
 * sowie „Datei hochladen" (Kategorie-Dialog). Wird auf der Detailseite UND im
 * Eingelangt-Wizard genutzt. Nach Upload/Löschen wird `changed` emittiert, damit der
 * Parent das Ticket neu lädt.
 */
const props = defineProps<{ ticketId: number; files: TicketFile[]; artikelfotoOnly?: boolean }>();
const emit  = defineEmits<{ (e: 'changed'): void }>();

const FILES_PREVIEW = 5;
const MAX_BATCH     = 5;
const uploadModal  = useTemplateRef<InstanceType<typeof FileUploadModal>>('uploadModal');
const confirmModal = useTemplateRef<InstanceType<typeof ConfirmModal>>('confirmModal');

const showAll  = ref(false);
const working  = ref(false);
const error    = ref<string | null>(null);

const displayedFiles = computed<TicketFile[]>(() =>
  showAll.value ? props.files : props.files.slice(0, FILES_PREVIEW),
);

async function onOpenFile(fileId: number): Promise<void> {
  error.value = null;
  try {
    const blob = await fetchFileBlob(props.ticketId, fileId);
    const url  = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Datei konnte nicht geöffnet werden.';
  }
}

async function onUpload(): Promise<void> {
  if (working.value) return;
  error.value = null;
  const changed = await uploadModal.value?.open(props.ticketId);
  if (changed) emit('changed');
}

/** Direkter Artikelfoto-Upload (Nur-Artikelfoto-Modus, ohne Kategorie-Dialog). */
async function onPickArtikelfotos(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const files = input.files ? Array.from(input.files) : [];
  input.value = ''; // erneutes Wählen derselben Datei erlauben
  if (!files.length || working.value) return;

  error.value = null;
  if (files.length > MAX_BATCH) {
    error.value = `Pro Upload sind maximal ${MAX_BATCH} Dateien möglich – nur die ersten ${MAX_BATCH} werden übernommen.`;
  }

  working.value = true;
  try {
    for (const file of files.slice(0, MAX_BATCH)) {
      await uploadFile(props.ticketId, file, 'artikelfoto');
    }
    emit('changed');
  } catch (e) {
    error.value = e instanceof Error ? `Upload fehlgeschlagen: ${e.message}` : 'Upload fehlgeschlagen.';
  } finally {
    working.value = false;
  }
}

async function onDelete(file: TicketFile): Promise<void> {
  if (working.value) return;
  const ok = await confirmModal.value?.open(`Datei „${file.description}" wirklich löschen?`);
  if (!ok) return;

  error.value   = null;
  working.value = true;
  try {
    await deleteFile(props.ticketId, file.id);
    emit('changed');
  } catch (e) {
    error.value = e instanceof Error ? `Löschen fehlgeschlagen: ${e.message}` : 'Löschen fehlgeschlagen.';
  } finally {
    working.value = false;
  }
}

function formatDate(value: string): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}
</script>

<style scoped>
/* .ticket-section / .ticket-section-title jetzt global in apple.css */

.files-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.files-upload-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.files-upload-btn {
  margin: 0;
  cursor: pointer;
  white-space: nowrap;
}

.files-upload-btn.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.files-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f1;
}

/* Klickbarer Bereich (öffnet die Datei): Name oben, Meta darunter — stapelt
   auf schmalen Displays sauber, keine Spalten-Überschneidung. */
.file-open {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
}

.file-name {
  color: var(--accent-hover, #e09600);
  font-weight: 600;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.file-meta {
  color: #6c757d;
  font-size: 13px;
}

.file-delete-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--muted, #6c757d);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.file-delete-btn:hover:not(:disabled) {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.file-delete-btn:disabled { opacity: 0.5; cursor: default; }

.files-empty {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

.files-more-btn {
  margin-top: 8px;
  padding: 0;
  text-decoration: none;
  color: var(--accent-hover, #e09600);
}

.file-error { margin: 10px 0 0; }
</style>
